import React, { createContext, useContext, useState } from 'react';
import Modal from '../components/common/Modal';

// Kontext pre správu modálnych okien
const ModalContext = createContext();

/**
 * Provider pre správu modálnych okien v celej aplikácii
 * 
 * @param {Object} props - Vlastnosti komponentu
 * @param {React.ReactNode} props.children - Obsah providera
 */
export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    content: null,
    onConfirm: null,
    confirmText: 'Potvrdiť',
    cancelText: 'Zrušiť',
    variant: 'default'
  });

  const openModal = ({
    title,
    content,
    onConfirm,
    confirmText = 'Potvrdiť',
    cancelText = 'Zrušiť',
    variant = 'default'
  }) => {
    setModalState({
      isOpen: true,
      title,
      content,
      onConfirm,
      confirmText,
      cancelText,
      variant
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  // Predpripravené modálne okná
  const confirmAction = (title, message, onConfirm, options = {}) => {
    openModal({
      title,
      content: <p>{message}</p>,
      onConfirm,
      variant: options.variant || 'warning',
      confirmText: options.confirmText || 'Potvrdiť',
      cancelText: options.cancelText || 'Zrušiť'
    });
  };

  // Funkcia, ktorá obalí onConfirm a po vykonaní automaticky zavrie modál
  const handleConfirm = () => {
    if (modalState.onConfirm) {
      try {
        modalState.onConfirm();
      } catch (err) {
        console.error('Chyba v onConfirm callbacku:', err);
      }
      closeModal();
    }
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, confirmAction }}>
      {children}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        onConfirm={handleConfirm}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        variant={modalState.variant}
      >
        {modalState.content}
      </Modal>
    </ModalContext.Provider>
  );
};

/**
 * Hook pre použitie modálnych okien v komponentoch
 * 
 * @returns {Object} - Objekt s funkciami openModal, closeModal a confirmAction
 */
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal musí byť použitý v rámci ModalProvider');
  }
  return context;
};
